/** @jsxImportSource @emotion/react */
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTheme } from '../provider/useTheme';
import { CoreButton } from './CoreButton';
import { TimeSelect } from './TimeSelect';
import { CoreIcon } from './CoreIcon';
import { Text } from './text';
import type { SemanticColorToken } from '../themes/types';
import type { MinuteStep } from './TimeSelect';
import type { TranslationValue } from './translation';

export interface CoreEventTimeCardProps {
  label?: string;
  labelPath?: string;
  labelValue?: TranslationValue;
  value?: dayjs.Dayjs | null;
  onChange?: (value: dayjs.Dayjs | null) => void;
  minuteStep?: MinuteStep;
  min?: dayjs.Dayjs | null;
  max?: dayjs.Dayjs | null;
  isDisabled?: boolean;
  disabledMessagePath?: string; // Translation path for disabled message (e.g., "Log start of the surgery first")
  disabledMessageValue?: TranslationValue;
  statusColor?: SemanticColorToken; // Color for the status indicator square
  isEditing?: boolean; // Controlled editing state
  onEditToggle?: (isEditing: boolean) => void;
  onNowClick?: () => void; // Handler for "Now" button
  nowLabelPath?: string; // Translation path for "Now" button
  earlierLabelPath?: string; // Translation path for "Earlier" button
}

export const CoreEventTimeCard: React.FC<CoreEventTimeCardProps> = memo(
  ({
    label,
    labelPath,
    labelValue,
    value,
    onChange,
    minuteStep = 15,
    min = null,
    max = null,
    isDisabled = false,
    disabledMessagePath = 'common:eventTimeCard.disabledMessage',
    disabledMessageValue,
    statusColor = 'fg.muted',
    isEditing: isEditingProp,
    onEditToggle,
    onNowClick,
    nowLabelPath = 'common:time.now',
    earlierLabelPath = 'common:time.earlier',
  }) => {
    // Time picker always uses 24-hour format for input/selection, regardless of user's display format preference
    const TIME_FORMAT = 'HH:mm';

    const theme = useTheme();
    const { getColor, getSpacing, getRadius } = theme;

    // Internal editing state if not controlled
    const [internalIsEditing, setInternalIsEditing] = useState(false);
    const [isEarlierMode, setIsEarlierMode] = useState(false);
    const isEditing = isEditingProp !== undefined ? isEditingProp : internalIsEditing;
    const prevValueRef = useRef<dayjs.Dayjs | null | undefined>(value);

    const handleEditToggle = useCallback(
      (newState: boolean) => {
        if (onEditToggle) {
          onEditToggle(newState);
        } else {
          setInternalIsEditing(newState);
        }
        // Reset earlier mode when closing edit
        if (!newState) {
          setIsEarlierMode(false);
        }
      },
      [onEditToggle],
    );

    // Auto-close edit mode when value changes from null to non-null
    useEffect(() => {
      if (isEditing && prevValueRef.current === null && value !== null) {
        handleEditToggle(false);
      }
      prevValueRef.current = value;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isEditing]);

    const handleNowClick = useCallback(() => {
      if (onNowClick) {
        onNowClick();
      } else {
        // Default behavior: set current time, respecting min/max
        const now = dayjs();

        // Check if now is within allowed range
        if (min && dayjs.isDayjs(min) && now.isBefore(min)) {
          // Now is before min, use min instead
          onChange?.(min);
        } else if (max && dayjs.isDayjs(max) && now.isAfter(max)) {
          // Now is after max, use max instead
          onChange?.(max);
        } else {
          // Now is within range
          onChange?.(now);
        }
      }
    }, [onNowClick, onChange, min, max]);

    const handleEarlierClick = useCallback(() => {
      setIsEarlierMode(true);
      handleEditToggle(true);
    }, [handleEditToggle]);

    // Styles
    const cardBg = getColor('card.bg.muted');
    const cardPadding = getSpacing('3');
    const cardRadius = getRadius('sm'); // Same as buttons
    const hasLabelPath = typeof labelPath === 'string' && labelPath.trim().length > 0;
    const hasLabel = typeof label === 'string' && label.trim().length > 0;
    const labelNode = hasLabelPath ? (
      <Text font="M_SemiBold" path={labelPath} value={labelValue} />
    ) : hasLabel ? (
      <Text font="M_SemiBold">{label}</Text>
    ) : null;

    // Locked/Disabled State - show lock icon and message
    if (isDisabled) {
      return (
        <div
          css={css`
            background-color: ${getColor('bg')};
            border: 1px solid ${getColor('border')};
            border-radius: ${cardRadius};
            padding: ${cardPadding};
            width: 100%;
          `}
        >
          <div
            css={css`
              margin-bottom: ${getSpacing('3')};
            `}
          >
            {labelNode}
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${getSpacing('2')};
              padding: ${getSpacing('3')};
              background-color: ${getColor('card.bg.muted')};
              border-radius: ${getRadius('xs')};
            `}
          >
            <CoreIcon icon="lock" size="md" color="fg" />
            <Text path={disabledMessagePath} value={disabledMessageValue} />
          </div>
        </div>
      );
    }

    if (isEditing) {
      // Calculate min/max for Earlier mode
      const effectiveMin = isEarlierMode ? (value || dayjs()).startOf('day') : min;
      const effectiveMax = isEarlierMode ? dayjs() : max;

      return (
        <div
          css={css`
            background-color: ${cardBg};
            border-radius: ${cardRadius};
            padding: ${cardPadding};
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: ${getSpacing('2')};
            `}
          >
            {labelNode}
            <CoreButton
              type="ghost"
              size="sm"
              iconOnly
              iconRight="close"
              ariaLabelPath="common:button.close"
              onClick={() => handleEditToggle(false)}
            />
          </div>
          <TimeSelect
            value={value}
            onChange={onChange}
            minuteStep={minuteStep}
            min={effectiveMin}
            max={effectiveMax}
            autoFocus={isEarlierMode}
          />
        </div>
      );
    }

    // View Mode - Empty State (no value)
    if (!value) {
      return (
        <div
          css={css`
            background-color: ${getColor('bg')};
            border: 1px solid ${getColor('border')};
            border-radius: ${cardRadius};
            padding: ${cardPadding};
            width: 100%;
          `}
        >
          <div
            css={css`
              margin-bottom: ${getSpacing('3')};
            `}
          >
            {labelNode}
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: 7fr 3fr;
              gap: ${getSpacing('2')};
            `}
          >
            <CoreButton type="primary.subtle" size="md" label={nowLabelPath} onClick={handleNowClick} />
            <CoreButton type="secondary" size="md" label={earlierLabelPath} onClick={handleEarlierClick} />
          </div>
        </div>
      );
    }

    // View Mode - Filled State (has value)
    return (
      <div
        css={css`
          background-color: ${cardBg};
          border-radius: ${cardRadius};
          padding: ${cardPadding};
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${getSpacing('2')};
          `}
        >
          {labelNode}
          <CoreButton
            type="ghost"
            size="sm"
            iconOnly
            iconRight="edit"
            ariaLabelPath="common:button.edit"
            onClick={() => handleEditToggle(true)}
          />
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${getSpacing('2')};
          `}
        >
          <div
            css={css`
              width: ${theme.sizes.layout.statusIndicatorSize}px;
              height: ${theme.sizes.layout.statusIndicatorSize}px;
              background-color: ${getColor(statusColor)};
              border-radius: ${getRadius('xs')};
            `}
          />
          <Text>{value.format(TIME_FORMAT)}</Text>
        </div>
      </div>
    );
  },
);

CoreEventTimeCard.displayName = 'CoreEventTimeCard';
